import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import store from '../../../store';
import { formatTime, sortPartialDate } from 'neuro-utils';
import { styled } from '@mui/system';
import colors from '../../../config/theme/colors';
import { formatPartialDate } from 'Utility/date';
import { filterDocumentsByType } from 'Utility/documentHandling';

/** 'patientReportedSeizure' is a my document type */
export type TSeizureDocumentType = SafeExtract<
  TDocumentType,
  'seizure' | 'patientReportedSeizure' | 'seizureFreePeriod'
>;

/** 'patientReportedSeizure' is a my document type */
export type TActualSeizureDocumentType = SafeExtract<TSeizureDocumentType, 'seizure' | 'patientReportedSeizure'>;

type TSecondaryDocumentType = SafeExtract<TDocumentType, 'seizureType'>;

export type TRelevantDocumentType = TSeizureDocumentType | TSecondaryDocumentType;

/**
 * Get date of the latest confirmed seizure.
 * @param documents Seizure documents from which to find the latest occurence date.
 * @returns Date of the latest seizure or undefined.
 */
export const getLastSeizureDate = (documents: IDocumentWithTypeV2<TSeizureDocumentType>[]): PartialDate | undefined => {
  const seizureDocs = filterDocumentsByType(documents, ['seizure', 'patientReportedSeizure']);
  const confirmedSeizures = filterConfirmedSeizures(seizureDocs);
  const sorted = confirmedSeizures.sort((a, b) => {
    const aDate = a._type === 'patientReportedSeizure' ? a.startDate : a.date;
    const bDate = b._type === 'patientReportedSeizure' ? b.startDate : b.date;
    return sortPartialDate(bDate, aDate);
  });
  // dumdum
  return sorted[0]?._type === 'patientReportedSeizure'
    ? (sorted[0].startDate ?? undefined)
    : (sorted[0]?.date ?? undefined);
};

/**
 * Get patient's own seizure types' classifications from store.
 * @returns An array of seizure type classifications (strings).
 */
export const getOwnSeizureTypesClassifications = (): Array<string> => {
  const sortedAndMergedDocuments = store.getState().documents.sortedAndMergedDocuments ?? [];

  const seizureTypeDocs = filterDocumentsByType(sortedAndMergedDocuments, ['seizureType']);
  const classifications = seizureTypeDocs.map(d => d.classification?.classification).filter(n => n) as string[];

  const uniqueClassifications = [...new Set(classifications)];
  return uniqueClassifications;
};

const isConfirmedSeizure = (
  d: IDocumentWithTypeV2<TSeizureDocumentType>,
): d is IDocumentWithTypeV2<TActualSeizureDocumentType> => 'status' in d && d.status === 'accepted';

/**
 * Filter out those seizure documents that are "not accepted"
 * @returns The remaining documents after filtering.
 */
export const filterConfirmedSeizures = (
  documents: IDocumentWithTypeV2<TSeizureDocumentType>[],
): Array<IDocumentWithTypeV2<TActualSeizureDocumentType>> => {
  return documents.filter(d => isConfirmedSeizure(d));
};

/**
 * Function for getting localized form of seizure classification
 * @param classification classification
 * @param shortOrLong This tells if we need just the short version of the classification, eg 2.1.1.1 Automatismit or if we want the long version, eg 2.1.1.1 Paikallisalkuinen kohtaus ilman tajunnanhämärtymistä / Motorisin oirein / Automatismit
 * @returns localized form of the classification
 */
export const formatSeizureClassification = (
  classification: IDocumentWithTypeV2<'seizure'>['classification'],
  shortOrLong: 'short' | 'long' = 'short',
  history?: boolean,
  textOnly?: boolean,
): string | React.JSX.Element => {
  // If the classification is undefined, return just a dash
  if (!classification) return '-';

  // If we need just the short form, return this
  if (shortOrLong === 'short') {
    return (
      <span>
        {!textOnly ? `${classification} ` : ''}
        <FormattedMessage
          id={`seizure.opts.${classification}`}
          values={{
            strong: chunks => (
              <span key={chunks.join('')} style={{ fontWeight: '600' }}>
                {React.Children.toArray(chunks)}
              </span>
            ),
          }}
        />
      </span>
    );
  } else {
    // Make array of classes of the classification. Eg. 2.1.1.1 -> [2.1, 2.1.1, 2.1.1.1]
    const parts = [];

    classification.length >= 6 && parts.push(classification.substring(0, 6));
    classification.length >= 4 && parts.push(classification.substring(0, 4));
    classification.length >= 2 && parts.push(classification.substring(0, 2));
    const partsReversed = parts.slice().reverse();
    return (
      <>
        {!textOnly ? `${classification} ` : ''}
        {partsReversed.map((p, i) => (
          <span key={p}>
            <FormattedMessage
              id={`seizure.opts.${p}`}
              values={{
                strong: chunks => (
                  <span key={chunks.join('')} style={{ fontWeight: '800' }}>
                    {React.Children.toArray(chunks)}
                  </span>
                ),
              }}
            />
            {i < parts.length - 1 ? ' / ' : ''}
            {history && <br />}
          </span>
        ))}
      </>
    );
  }
};

/**
 * Format row data.
 * @param key Data field's name.
 * @param value Data field's value.
 * @returns Value formatted.
 */
export const formatRowData = (key: string, value: any): React.ReactNode => {
  let result: React.ReactNode;
  const noData = '-';
  if (value) {
    switch (key) {
      case 'date':
        result = formatPartialDate(value);
        break;
      case 'time':
        result = formatTime(value);
        break;
      case 'medicCompliance':
        result = <FormattedMessage id={`general.${value}`} />;
        break;
      case 'seizureFreePeriod':
        result = !value[1]
          ? formatPartialDate(value[0])
          : `${formatPartialDate(value[0])} - ${formatPartialDate(value[1])}`;
        break;
      case 'seizureFreePeriodLength':
        result = typeof value === 'object' && (
          <React.Fragment>
            <FormattedMessage values={{ N: value.years }} id={'general.years'} />{' '}
            <FormattedMessage values={{ N: value.months }} id={'general.months'} />{' '}
            <FormattedMessage values={{ N: value.days }} id={'general.days'} />
          </React.Fragment>
        );
        break;
      case 'classification':
        result = formatSeizureClassification(value, 'long', true);
        break;
      default:
        result = value;
        break;
    }
  }
  return !result || result === '' ? noData : result;
};

/**
 * Indent options that are subclass of another number e.g. 1.0.1 is intended under 1.0
 * @param o
 * @returns Intended option if subnumber else default
 */
export const formatOptionIndent = (o: string): React.JSX.Element => {
  const classificationString = o;
  const regex = /^[0-9]\.[0-9]\.[0-9]\.[0-9]/;
  const regex2 = /^[0-9]\.[0-9]\.[0-9]\s/;
  const indentOption = classificationString && classificationString.match(regex);
  const indentOption2 = classificationString && classificationString.match(regex2);
  const splittedAndShifted = indentOption && classificationString.split(' ');
  splittedAndShifted && splittedAndShifted.shift();
  const splittedAndShifted2 = indentOption2 && classificationString.split(' ');
  splittedAndShifted2 && splittedAndShifted2.shift();
  const final = splittedAndShifted && splittedAndShifted.join(' ');
  const final2 = splittedAndShifted2 && splittedAndShifted2.join(' ');
  const indentedOption = splittedAndShifted && `${indentOption} ${final}`;
  const indentedOption2 = splittedAndShifted2 && `${indentOption2} ${final2}`;
  if (indentOption) {
    return (
      <React.Fragment key={classificationString}>
        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
        {formatSeizureClassification(indentedOption?.split(' ').at(0) as TSeizureClassification, 'short')}
      </React.Fragment>
    );
  } else if (indentOption2) {
    return (
      <React.Fragment key={classificationString}>
        &nbsp;&nbsp;&nbsp;
        {formatSeizureClassification(indentedOption2?.split(' ').at(0) as TSeizureClassification, 'short')}
      </React.Fragment>
    );
  } else {
    return (
      <React.Fragment key={classificationString}>
        {formatSeizureClassification(classificationString.split(' ').at(0) as TSeizureClassification, 'short')}
      </React.Fragment>
    );
  }
};

export const GroupHeader = styled('div')({
  position: 'sticky' as const,
  height: '3.6rem',
  fontSize: '1.0rem',

  top: '-1px', // Counter padding and sticky needs this
  paddingTop: '0.7rem',
  color: colors.darkGray,
  backgroundColor: colors.white,
  alignItems: 'center',
  marginTop: '0rem',
  '& .MuiMenuItem-root.Mui-disabled': {
    opacity: 1,
    fontSize: '1.4rem',
  },
  zIndex: 100,
});
